import {
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProPaymentScheduleEnum,
} from "../../../../graphql/types";
import type { ChamberMemberProProratedSignupPriceReturn } from "../../../../graphql/types";
import {
  getMembershipBasePrice,
  getMembershipEffectivePrice,
} from "../memberMembershipPricing";

// Create Member Step 4 fee preview. Kept free of page imports so it can be
// unit tested; it must match the backend membership invoice pricing.

export type MembershipPricingModel = "fixed" | "employeeRange";

export type MockMembershipModel = {
  id: string;
  code: string;
  name: string;
  description: string;
  pricingModel: MembershipPricingModel;
  annualPrice: number;
  monthlyPrice?: number;
  paymentResponsibility?: ChamberMemberProMembershipModelPaymentResponsibilityEnum;
  thirdPartyPayerName?: string | null;
  thirdPartyPaymentNotes?: string | null;
  // Chamber-wide fees this type skips (matches backend pricing).
  excludeOneTimeOnboardingFee?: boolean;
  excludeProvincialChamberFee?: boolean;
  // Registration Charity Number field (CAP flag from the type name).
  collectsCharityRegistrationNumber?: boolean;
  additionalCosts: {
    id: string;
    code: string;
    name: string;
    description: string;
    // BILL-003: add-ons are always billed/displayed at the full (annual)
    // amount — the settings' monthlyAmountInDollars is ignored by billing.
    annualAmount: number;
    enabled: boolean;
    // No tax on this add-on (Chamber Set Up).
    taxExempt?: boolean;
  }[];
  ranges: {
    id?: string;
    code?: string;
    from: number;
    to?: number;
    annualPrice: number;
    monthlyPrice?: number;
  }[];
};

export type FeeBreakdown = {
  baseAnnualAmount: number;
  membershipInvestmentAmount: number;
  membershipCode: string;
  additionalCosts: number;
  membershipDiscountAmount: number;
  onboardingFee: number;
  provincialChamberFee: number;
  proratedDiscount: number;
  taxAmount: number;
  currentAmountDue: number;
  // BILL-002/003 split: monthly memberships get ONE invoice for the full
  // membership total (12 installments) and add-ons get a SECOND full-amount
  // invoice, so the step shows these two figures instead of a blended total.
  membershipInvoiceTotal: number;
  addOnInvoiceTotal: number;
  renewalAmount: number;
  renewalDate: string;
};

export const getNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export const getTaxAmount = (amount: number, taxRatePercentage: number) =>
  Math.round(amount * (taxRatePercentage / 100) * 100) / 100;

export const calculateFeeBreakdown = (
  membershipTypeId: string,
  fullTimeEmployees: string,
  partTimeEmployees: string,
  selectedAdditionalCostIds: string[],
  paymentSchedule: ChamberMemberProPaymentScheduleEnum | "",
  membershipDiscountAmount: string,
  membershipInvoiceTaxRatePercentage: number,
  membershipModels: MockMembershipModel[],
  partTimeEmployeesPerFullTime?: number,
  proratedSignupPricingEnabled?: boolean,
  proratedSignupPrices?: Pick<
    ChamberMemberProProratedSignupPriceReturn,
    "month" | "priceInDollars" | "pricingKey"
  >[],
  oneTimeOnboardingFeeInDollars = 0,
  membershipBaseIsNoCharge = false,
  provincialChamberFeeInDollars = 0,
): FeeBreakdown => {
  const membershipModel = membershipModels.find(
    (model) => model.id === membershipTypeId,
  );

  if (!membershipModel) {
    return {
      baseAnnualAmount: 0,
      membershipInvestmentAmount: 0,
      membershipCode: "-",
      additionalCosts: 0,
      membershipDiscountAmount: 0,
      onboardingFee: 0,
      provincialChamberFee: 0,
      proratedDiscount: 0,
      taxAmount: 0,
      currentAmountDue: 0,
      membershipInvoiceTotal: 0,
      addOnInvoiceTotal: 0,
      renewalAmount: 0,
      renewalDate: "Apr 30, 2026",
    };
  }

  const isMonthly =
    paymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly;
  const baseMembershipPrice = getMembershipBasePrice({
    fullTimeEmployees,
    membershipModel,
    partTimeEmployees,
    partTimeEmployeesPerFullTime,
    paymentSchedule,
  });
  const effectiveMembershipPrice = getMembershipEffectivePrice({
    basePrice: baseMembershipPrice,
    paymentSchedule,
    proratedSignupPricingEnabled,
    proratedSignupPrices,
  });
  const baseAnnualAmount = baseMembershipPrice.amountInDollars;
  const membershipInvestmentAmount = effectiveMembershipPrice.amountInDollars;
  const chargeableBaseAnnualAmount = membershipBaseIsNoCharge
    ? 0
    : baseAnnualAmount;
  const onboardingFee = membershipModel.excludeOneTimeOnboardingFee
    ? 0
    : Math.max(0, oneTimeOnboardingFeeInDollars);
  // Same rule as the backend invoice: a taxed yearly line, none for a
  // no-charge membership or a type that skips it. Monthly plans carry it
  // once, on the membership invoice.
  const provincialChamberFee =
    membershipBaseIsNoCharge || membershipModel.excludeProvincialChamberFee
      ? 0
      : Math.max(0, provincialChamberFeeInDollars);
  // BILL-003: add-on benefits are ALWAYS full (annual) amount regardless of
  // the payment schedule — they are never part of a monthly installment plan.
  const selectedAdditionalCosts = membershipModel.additionalCosts.filter(
    (cost) => cost.enabled && selectedAdditionalCostIds.includes(cost.id),
  );
  const additionalCosts = selectedAdditionalCosts.reduce(
    (total, cost) => total + cost.annualAmount,
    0,
  );
  // Tax-exempt add-ons are billed without tax (Chamber Set Up add-on option).
  const taxExemptAdditionalCosts = selectedAdditionalCosts
    .filter((cost) => cost.taxExempt)
    .reduce((total, cost) => total + cost.annualAmount, 0);
  const taxableAdditionalCosts = additionalCosts - taxExemptAdditionalCosts;
  // BILL-002: monthly memberships are invoiced ONCE for the full membership
  // total (monthly price annualized over the 12-installment plan).
  const membershipFullAmount = isMonthly
    ? chargeableBaseAnnualAmount * 12
    : chargeableBaseAnnualAmount;
  const normalizedMembershipDiscount = Math.min(
    getNumber(membershipDiscountAmount),
    membershipFullAmount + (isMonthly ? 0 : additionalCosts),
  );
  const proratedDiscount = membershipBaseIsNoCharge
    ? 0
    : effectiveMembershipPrice.proratedDiscountInDollars;
  // Membership invoice (monthly: membership-related lines only; the discount
  // stays on the membership invoice).
  const membershipTaxableAmount =
    Math.max(
      membershipFullAmount - proratedDiscount - normalizedMembershipDiscount,
      0,
    ) +
    provincialChamberFee +
    onboardingFee;
  const membershipInvoiceTax = getTaxAmount(
    membershipTaxableAmount,
    membershipInvoiceTaxRatePercentage,
  );
  const membershipInvoiceTotal =
    Math.round((membershipTaxableAmount + membershipInvoiceTax) * 100) / 100;
  // Add-on invoice (spec §8: every schedule when add-ons are selected): full
  // add-on amounts plus per-line tax, no payment plan.
  const addOnInvoiceTax = getTaxAmount(
    taxableAdditionalCosts,
    membershipInvoiceTaxRatePercentage,
  );
  const addOnInvoiceTotal =
    Math.round((additionalCosts + addOnInvoiceTax) * 100) / 100;
  const taxableAmount = isMonthly
    ? membershipTaxableAmount + taxableAdditionalCosts
    : Math.max(
        chargeableBaseAnnualAmount +
          taxableAdditionalCosts -
          proratedDiscount -
          normalizedMembershipDiscount,
        0,
      ) +
      provincialChamberFee +
      onboardingFee;
  const taxAmount = isMonthly
    ? membershipInvoiceTax + addOnInvoiceTax
    : getTaxAmount(taxableAmount, membershipInvoiceTaxRatePercentage);
  const currentAmountDue = taxableAmount + taxExemptAdditionalCosts + taxAmount;
  const renewalTaxableAmount =
    chargeableBaseAnnualAmount +
    taxableAdditionalCosts +
    (isMonthly ? 0 : provincialChamberFee);
  const renewalAmount =
    Math.round(
      (renewalTaxableAmount +
        taxExemptAdditionalCosts +
        getTaxAmount(
          renewalTaxableAmount,
          membershipInvoiceTaxRatePercentage,
        )) *
        100,
    ) / 100;

  return {
    baseAnnualAmount,
    membershipInvestmentAmount,
    membershipCode: baseMembershipPrice.code,
    additionalCosts,
    membershipDiscountAmount: normalizedMembershipDiscount,
    onboardingFee,
    provincialChamberFee,
    proratedDiscount,
    taxAmount,
    currentAmountDue,
    membershipInvoiceTotal,
    addOnInvoiceTotal,
    renewalAmount,
    renewalDate: "Apr 30, 2026",
  };
};
